"use client";

import { CalculatorSection } from "@/components/calculators/shared/calculator-workbench";
import { calculateFit } from "@/lib/calculators/truck-loading-merged/calculate-fit";
import type {
  TruckSpec,
  ItemDimensions,
  LoadingType,
} from "@/lib/calculators/truck-loading-merged/types";
import { SelectableCard } from "@/components/ui/selectable-card";
import { LOADING_PATTERN_DIAGRAMS } from "./loading-pattern-diagrams";

const LOADING_TYPES: Array<{ id: LoadingType; label: string }> = [
  { id: "straight", label: "Straight" },
  { id: "turned", label: "Turned" },
  { id: "pinwheel", label: "Pinwheeled" },
];

function computeOptimizedCount(maxTotal: number, targetQty: number): number {
  if (maxTotal <= 0 || targetQty <= 0) return 0;
  const trucksNeeded = Math.ceil(targetQty / maxTotal);
  return Math.ceil(targetQty / trucksNeeded);
}

function loadingTypeCounts(
  truck: TruckSpec,
  item: ItemDimensions,
  targetQty: number | null,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const type of LOADING_TYPES) {
    const fitResult = calculateFit(truck, item, type.id);
    const maxTotal = fitResult.totalFits;
    counts[type.id] =
      targetQty !== null ? computeOptimizedCount(maxTotal, targetQty) : maxTotal;
  }
  return counts;
}

function LoadingPatternHint({
  canCalc,
  value,
  bestType,
  bestCount,
}: {
  canCalc: boolean;
  value: LoadingType;
  bestType: LoadingType;
  bestCount: number | undefined;
}) {
  if (!canCalc) {
    return (
      <p className="text-sm text-muted-foreground mb-3">
        Enter dimensions to compare loading types
      </p>
    );
  }
  if (value === bestType) {
    return (
      <p className="text-sm text-success-strong font-medium mb-3">
        Maximum efficiency selected
      </p>
    );
  }
  return (
    <p className="text-sm text-success-strong font-medium mb-3">
      Switch to{" "}
      {
        LOADING_TYPES.find((loadingType) => loadingType.id === bestType)
          ?.label
      }{" "}
      for maximum output ({bestCount} units)
    </p>
  );
}

function countClassName(selected: boolean, isBest: boolean): string {
  if (selected) return "text-action";
  return isBest ? "text-success-strong" : "text-foreground";
}

function LoadingTypeCard({
  type,
  selected,
  isBest,
  count,
  showCount,
  onSelect,
}: {
  type: { id: LoadingType; label: string };
  selected: boolean;
  isBest: boolean;
  count: number | undefined;
  showCount: boolean;
  onSelect: () => void;
}) {
  const Diagram = LOADING_PATTERN_DIAGRAMS[type.id];
  return (
    <SelectableCard
      size="compact"
      isSelected={selected}
      aria-pressed={selected}
      onClick={onSelect}
      className="w-28 justify-between"
    >
      <div className="w-16 h-28">
        <Diagram />
      </div>
      <span
        className={`text-xs font-semibold ${selected ? "text-action" : "text-muted-foreground"}`}
      >
        {type.label}
      </span>
      {showCount && (
        <span
          className={`text-lg font-bold tabular-nums ${countClassName(selected, isBest)}`}
        >
          {count}
        </span>
      )}
    </SelectableCard>
  );
}

interface LoadingTypeSelectorProps {
  value: LoadingType;
  onChange: (type: LoadingType) => void;
  truck: TruckSpec;
  item: ItemDimensions;
  bestType: LoadingType;
  specificQtyEnabled?: boolean;
  parsedTargetQty?: number;
}

export function LoadingTypeSelector({
  value,
  onChange,
  truck,
  item,
  bestType,
  specificQtyEnabled = false,
  parsedTargetQty = 0,
}: LoadingTypeSelectorProps) {
  const canCalc = item.lengthIn > 0 && item.widthIn > 0 && item.heightIn > 0;
  const targetQty =
    specificQtyEnabled && parsedTargetQty > 0 ? parsedTargetQty : null;
  const counts: Record<string, number> = canCalc
    ? loadingTypeCounts(truck, item, targetQty)
    : {};

  return (
    <CalculatorSection title="Loading pattern">
      <div>
        <LoadingPatternHint
          canCalc={canCalc}
          value={value}
          bestType={bestType}
          bestCount={counts[bestType]}
        />
        <div className="flex flex-wrap items-stretch gap-4">
          {LOADING_TYPES.map((type) => (
            <LoadingTypeCard
              key={type.id}
              type={type}
              selected={value === type.id}
              isBest={type.id === bestType}
              count={counts[type.id]}
              showCount={canCalc}
              onSelect={() => onChange(type.id)}
            />
          ))}
        </div>
      </div>
    </CalculatorSection>
  );
}
